/**
 * @component ry-copy-button
 * @category  feedback
 * @summary   One-click clipboard button. Click writes [value] (or the textContent / attribute / property of the element referenced by [from]) to the system clipboard, then briefly flashes a success (or error) state via `data-ry-state` before reverting to `idle`.
 *
 * Port of Shoelace's `<sl-copy-button>` (MIT) — see ATTRIBUTION below.
 *
 * @attribute value              string?   default=""    Literal text to copy when clicked. Ignored if [from] resolves.
 * @attribute from               string?                Element id whose content to copy. Defaults to `textContent`. Use `from="el[attr]"` to copy an attribute, `from="el.prop"` to copy a property. Takes precedence over [value].
 * @attribute disabled           boolean   default=false   Non-interactive. `aria-disabled="true"`, blocks click + keyboard.
 * @attribute copy-label         string?                Aria label / tooltip text for the idle state. Defaults to "Copy".
 * @attribute success-label      string?                Aria label / tooltip text for the success state. Defaults to "Copied".
 * @attribute error-label        string?                Aria label / tooltip text for the error state. Defaults to "Error".
 * @attribute feedback-duration  number    default=1000  Milliseconds the success/error state stays visible before reverting to idle.
 * @attribute tooltip-placement  string?   default=top   top | right | bottom | left — forwarded to a paired `<ry-tooltip>` if present.
 * @attribute hoist              boolean   default=false  Forwarded to a paired tooltip — positions the floating layer in `<body>` to escape `overflow:hidden` ancestors.
 *
 * @property  value             string   Mirrors the attribute.
 * @property  from              string   Mirrors the attribute.
 * @property  disabled          boolean  Mirrors the attribute.
 * @property  copyLabel         string   Mirrors `copy-label`.
 * @property  successLabel      string   Mirrors `success-label`.
 * @property  errorLabel        string   Mirrors `error-label`.
 * @property  feedbackDuration  number   Mirrors `feedback-duration`.
 *
 * @event ry:beforecopy
 *        detail: { value: string }
 *        fires:  on click / Enter / Space, after the text to copy is resolved ([value] or [from]) and before `navigator.clipboard.writeText()` is called. `detail.value` is the exact string about to be written.
 *        cancelable: preventDefault() skips the clipboard write and the success flash; the state stays `idle` and neither `ry:copy` nor `ry:error` fires
 *
 * @event ry:copy
 *        detail: { value: string }
 *        fires:  every successful clipboard write. `value` is the exact string written.
 *
 * @event ry:error
 *        detail: { error: Error | string }
 *        fires:  when there is nothing to copy, the [from] target is missing, or `navigator.clipboard.writeText()` rejects.
 *
 * @keyboard Enter, Space — activate copy (when not disabled).
 *
 * @state  idle    — default. Default copy icon is visible.
 * @state  success — flashes after a successful copy for [feedback-duration] ms. Success icon is visible.
 * @state  error   — flashes when the copy fails for [feedback-duration] ms. Error icon is visible.
 *
 * @content Slot defaults are auto-injected if absent:
 *   - `[data-ry-target="copy-icon"]`     — visible in `idle` state. Default: `<ry-icon name="copy">`.
 *   - `[data-ry-target="success-icon"]`  — visible in `success` state. Default: `<ry-icon name="check">`.
 *   - `[data-ry-target="error-icon"]`    — visible in `error` state. Default: `<ry-icon name="error">`.
 *
 * @dom
 *   ry-copy-button[value?][from?][disabled?][copy-label?][success-label?][error-label?][feedback-duration?]   role="button"  aria-live="polite"  data-ry-state="idle|success|error"
 *     > span[data-ry-target="copy-icon"]    > <ry-icon name="copy">
 *     > span[data-ry-target="success-icon"] > <ry-icon name="check">
 *     > span[data-ry-target="error-icon"]   > <ry-icon name="error">
 *
 * @gotcha  `from` takes precedence over `value`. If [from] is set but the
 *          referenced element doesn't exist (or yields empty text), the
 *          component flashes `error` and fires `ry:error` — it does NOT fall
 *          back to [value].
 *
 * @gotcha  `navigator.clipboard` requires a secure context (HTTPS or
 *          localhost) AND a user gesture. Programmatic `.click()` from a
 *          background timer may reject. The component fires `ry:error` in
 *          that case so consumers can surface their own message.
 *
 * @gotcha  While a feedback timer is running, additional clicks are ignored
 *          (no overlapping state flashes). The handler returns early until
 *          the state reverts to `idle`.
 *
 * @example basic
 * ```html
 * <ry-copy-button value="Hello, world!"></ry-copy-button>
 * ```
 *
 * @example from-element-text
 * ```html
 * <p id="src">Snippet to copy</p>
 * <ry-copy-button from="src"></ry-copy-button>
 * ```
 *
 * @example from-attribute
 * ```html
 * <input id="email" value="hi@example.com" readonly>
 * <ry-copy-button from="email[value]"></ry-copy-button>
 * ```
 *
 * @example custom-labels
 * ```html
 * <ry-copy-button
 *   value="ry-ui rocks"
 *   copy-label="Copy snippet"
 *   success-label="Snippet copied!"
 *   error-label="Couldn't copy"
 * ></ry-copy-button>
 * ```
 *
 * @example disabled
 * ```html
 * <ry-copy-button value="nope" disabled></ry-copy-button>
 * ```
 *
 * @example custom-icons
 * ```html
 * <ry-copy-button value="custom">
 *   <span data-ry-target="copy-icon"><ry-icon name="download"></ry-icon></span>
 *   <span data-ry-target="success-icon"><ry-icon name="check"></ry-icon></span>
 *   <span data-ry-target="error-icon"><ry-icon name="error"></ry-icon></span>
 * </ry-copy-button>
 * ```
 *
 * @tutorial click-writes-value-and-fires-ry-copy
 * ```js
 * // navigator.clipboard.writeText is mocked in the play runner.
 * const writes = [];
 * navigator.clipboard = { writeText: (v) => { writes.push(v); return Promise.resolve(); } };
 *
 * const el = mount('<ry-copy-button value="hello"></ry-copy-button>');
 * await expectEvent(el, 'ry:copy', () => el.click());
 * await expectEqual(writes[0], 'hello', 'wrote value to clipboard');
 * await expectEqual(el.dataset.ryState, 'success', 'transitions to success');
 * ```
 *
 * @tutorial from-id-takes-precedence-over-value
 * ```js
 * const writes = [];
 * navigator.clipboard = { writeText: (v) => { writes.push(v); return Promise.resolve(); } };
 *
 * const root = mount('<div><p id="src">from-text</p><ry-copy-button from="src" value="ignored"></ry-copy-button></div>');
 * const btn = root.querySelector('ry-copy-button');
 * await expectEvent(btn, 'ry:copy', () => btn.click());
 * await expectEqual(writes[0], 'from-text', 'from textContent wins over value');
 * ```
 *
 * @tutorial disabled-blocks-click
 * ```js
 * const writes = [];
 * navigator.clipboard = { writeText: (v) => { writes.push(v); return Promise.resolve(); } };
 *
 * const el = mount('<ry-copy-button value="x" disabled></ry-copy-button>');
 * let fired = false;
 * el.addEventListener('ry:copy', () => { fired = true; });
 * el.click();
 * await expectEqual(fired, false, 'disabled suppresses copy');
 * await expectEqual(writes.length, 0, 'clipboard not touched');
 * await expectAttr(el, 'aria-disabled', 'true');
 * ```
 *
 * @tutorial beforecopy-veto
 * ```js
 * // preventDefault() on ry:beforecopy skips the clipboard write: the state
 * // stays idle and ry:copy / ry:error stay silent. Lift the veto and the
 * // same click copies.
 * const el = mount('<ry-copy-button value="hello"></ry-copy-button>');
 * const fired = [];
 * el.addEventListener('ry:copy', () => fired.push('copy'));
 * el.addEventListener('ry:error', () => fired.push('error'));
 * let seen = null;
 * el.addEventListener('ry:beforecopy', (e) => { seen = e.detail; e.preventDefault(); }, { once: true });
 * el.click();
 * await sleep(20);
 * await expectEqual(seen.value, 'hello', 'detail.value is the text about to be written');
 * await expectEqual(fired.length, 0, 'neither ry:copy nor ry:error fired');
 * await expectEqual(el.dataset.ryState, 'idle', 'state stays idle');
 * await expectEvent(el, 'ry:copy', () => el.click());
 * await expectEqual(el.dataset.ryState, 'success', 'without the veto, the copy proceeds');
 * ```
 *
 * @tutorial error-when-from-target-missing
 * ```js
 * navigator.clipboard = { writeText: () => Promise.resolve() };
 * const el = mount('<ry-copy-button from="does-not-exist"></ry-copy-button>');
 * await expectEvent(el, 'ry:error', () => el.click());
 * await expectEqual(el.dataset.ryState, 'error');
 * ```
 *
 * @see ry-button
 * @see ry-tooltip
 * @see ry-icon
 */

// ─── ATTRIBUTION ────────────────────────────────────────────────────────
// Behavior, attribute surface, and state machine adapted from Shoelace's
// `<sl-copy-button>` (https://shoelace.style/components/copy).
// Source: https://github.com/shoelace-style/shoelace
// License: MIT — Copyright (c) 2020 A Beautiful Site, LLC
// See LICENSE-THIRD-PARTY.md for the full notice.
// ─────────────────────────────────────────────────────────────────────────

import { RyElement } from '../core/ry-element.js';
import { getIcon } from '../core/ry-icons.js';

export type RyCopyButtonState = 'idle' | 'success' | 'error';

export interface RyCopyDetail { value: string }
export interface RyCopyErrorDetail { error: Error | string }

declare global {
  interface HTMLElementEventMap {
    'ry:copy': CustomEvent<RyCopyDetail>;
    'ry:error': CustomEvent<RyCopyErrorDetail>;
  }
}

export class RyCopyButton extends RyElement {
  static get observedAttributes(): string[] {
    return ['disabled', 'copy-label', 'success-label', 'error-label'];
  }

  #feedbackTimer: ReturnType<typeof setTimeout> | null = null;

  setup(): void {
    if (!this.hasAttribute('role')) this.setAttribute('role', 'button');
    if (!this.hasAttribute('aria-live')) this.setAttribute('aria-live', 'polite');
    this.#ensureSlots();
    this.#applyState('idle');
    this.#applyTabIndex();
    this.#applyAriaLabel();

    this.on(this, 'click', this.#handleClick);
    this.on(this, 'keydown', this.#handleKeydown);
  }

  teardown(): void {
    if (this.#feedbackTimer != null) {
      clearTimeout(this.#feedbackTimer);
      this.#feedbackTimer = null;
    }
  }

  attributeChangedCallback(name: string, oldValue: string | null, newValue: string | null): void {
    if (oldValue === newValue) return;
    if (!this.hasAttribute('data-ry-initialized')) return;
    if (name === 'disabled') this.#applyTabIndex();
    if (name === 'disabled' || name.endsWith('-label')) this.#applyAriaLabel();
  }

  // ─── Public API ─────────────────────────────────────────────────────
  get value(): string { return this.getAttribute('value') ?? ''; }
  set value(v: string) { this.setAttribute('value', v); }

  get from(): string { return this.getAttribute('from') ?? ''; }
  set from(v: string) {
    if (v) this.setAttribute('from', v);
    else this.removeAttribute('from');
  }

  get disabled(): boolean { return this.hasAttribute('disabled'); }
  set disabled(v: boolean) {
    if (v) this.setAttribute('disabled', '');
    else this.removeAttribute('disabled');
  }

  get copyLabel(): string { return this.getAttribute('copy-label') ?? 'Copy'; }
  set copyLabel(v: string) { this.setAttribute('copy-label', v); }

  get successLabel(): string { return this.getAttribute('success-label') ?? 'Copied'; }
  set successLabel(v: string) { this.setAttribute('success-label', v); }

  get errorLabel(): string { return this.getAttribute('error-label') ?? 'Error'; }
  set errorLabel(v: string) { this.setAttribute('error-label', v); }

  get feedbackDuration(): number {
    const raw = this.getAttribute('feedback-duration');
    const n = raw == null ? NaN : Number(raw);
    return Number.isFinite(n) && n >= 0 ? n : 1000;
  }
  set feedbackDuration(v: number) { this.setAttribute('feedback-duration', String(v)); }

  // ─── Internals ──────────────────────────────────────────────────────
  #ensureSlots(): void {
    // Auto-inject defaults only if the consumer did NOT supply that slot.
    const inject = (target: 'copy-icon' | 'success-icon' | 'error-icon', glyph: string): void => {
      if (this.querySelector(`[data-ry-target="${target}"]`)) return;
      const span = document.createElement('span');
      span.setAttribute('data-ry-target', target);
      span.className = `ry-copy-button__${target}`;
      // Prefer the registered <ry-icon> custom element so theme/icon-pack
      // swaps still work; fall back to raw SVG if the registry is missing.
      const iconName = target === 'copy-icon' ? 'copy' : target === 'success-icon' ? 'check' : 'error';
      const svg = getIcon(iconName);
      if (svg) {
        span.innerHTML = `<ry-icon name="${iconName}"></ry-icon>`;
      } else {
        span.innerHTML = glyph;
      }
      this.appendChild(span);
    };

    inject('copy-icon', '');
    inject('success-icon', '');
    inject('error-icon', '');
  }

  #applyTabIndex(): void {
    const inert = this.disabled;
    if (!this.hasAttribute('tabindex') || this.dataset.ryTabindexManaged) {
      this.setAttribute('tabindex', inert ? '-1' : '0');
      this.dataset.ryTabindexManaged = '1';
    }
    if (inert) this.setAttribute('aria-disabled', 'true');
    else this.removeAttribute('aria-disabled');
  }

  #applyAriaLabel(): void {
    // Cycle aria-label with state so screen readers announce the right thing.
    // Don't clobber a user-set aria-label (no ry-managed marker).
    if (this.hasAttribute('aria-label') && !this.dataset.ryLabelManaged) return;
    const state = (this.dataset.ryState as RyCopyButtonState | undefined) ?? 'idle';
    const label =
      state === 'success' ? this.successLabel :
      state === 'error'   ? this.errorLabel   :
      this.copyLabel;
    this.setAttribute('aria-label', label);
    this.dataset.ryLabelManaged = '1';
  }

  #applyState(next: RyCopyButtonState): void {
    this.dataset.ryState = next;
    this.#applyAriaLabel();
  }

  #handleClick = (event: MouseEvent): void => {
    if (this.disabled) {
      event.preventDefault();
      event.stopImmediatePropagation();
      return;
    }
    // Drop overlapping clicks while feedback is flashing.
    if (this.#feedbackTimer != null) {
      event.preventDefault();
      return;
    }
    void this.#doCopy();
  };

  #handleKeydown = (event: KeyboardEvent): void => {
    if (event.key !== 'Enter' && event.key !== ' ') return;
    if (this.disabled) {
      event.preventDefault();
      return;
    }
    event.preventDefault();
    this.click();
  };

  async #doCopy(): Promise<void> {
    let toCopy = this.value;

    if (this.from) {
      const parsed = this.#parseFrom(this.from);
      const root = this.getRootNode() as Document | ShadowRoot;
      const target =
        'getElementById' in root
          ? root.getElementById(parsed.id)
          : document.getElementById(parsed.id);

      if (!target) {
        this.#flashError(new Error(`ry-copy-button: no element with id "${parsed.id}"`));
        return;
      }
      if (parsed.kind === 'attribute') {
        toCopy = target.getAttribute(parsed.field) ?? '';
      } else if (parsed.kind === 'property') {
        // @ts-expect-error — dynamic property read
        toCopy = String(target[parsed.field] ?? '');
      } else {
        toCopy = target.textContent ?? '';
      }
    }

    if (!toCopy) {
      this.#flashError(new Error('ry-copy-button: nothing to copy'));
      return;
    }

    if (!this.emit<RyCopyDetail>('beforecopy', { value: toCopy })) return;

    try {
      const clip = navigator.clipboard;
      if (!clip || typeof clip.writeText !== 'function') {
        throw new Error('clipboard API unavailable');
      }
      await clip.writeText(toCopy);
      this.#flashSuccess(toCopy);
    } catch (err) {
      this.#flashError(err as Error);
    }
  }

  #parseFrom(from: string): { id: string; field: string; kind: 'text' | 'attribute' | 'property' } {
    const t = from.trim();
    if (t.includes('.')) {
      const [id, ...rest] = t.split('.');
      return { id: id ?? '', field: rest.join('.'), kind: 'property' };
    }
    if (t.includes('[') && t.endsWith(']')) {
      const [id, field] = t.replace(/\]$/, '').split('[');
      return { id: id ?? '', field: field ?? '', kind: 'attribute' };
    }
    return { id: t, field: '', kind: 'text' };
  }

  #flashSuccess(value: string): void {
    this.#applyState('success');
    this.emit<RyCopyDetail>('copy', { value });
    this.#scheduleRevert();
  }

  #flashError(error: Error | string): void {
    this.#applyState('error');
    this.emit<RyCopyErrorDetail>('error', { error });
    this.#scheduleRevert();
  }

  #scheduleRevert(): void {
    if (this.#feedbackTimer != null) clearTimeout(this.#feedbackTimer);
    this.#feedbackTimer = setTimeout(() => {
      this.#feedbackTimer = null;
      this.#applyState('idle');
    }, this.feedbackDuration);
  }
}

customElements.define('ry-copy-button', RyCopyButton);
